/**
 * Motor próprio do "Jarvis" (spec 011, fase 5). Puro: recebe o resultado
 * FINAL do Vosk em modo gramática (só a palavra-chave ou `[unk]`) e diz quais
 * palavras contam como disparo.
 *
 * Com gramática o Vosk sempre "encaixa" alguma coisa, então a confiança da
 * palavra é o que separa disparo de ruído. Abaixo do limite não conta, mas
 * volta marcado (`aceito: false`) pra calibrar na tela do laboratório.
 */

export interface VoskWord {
  word: string;
  conf: number;
  /** Começo da palavra, em segundos de áudio desde que o reconhecedor começou. */
  start?: number;
  /** Fim da palavra, em segundos de áudio desde que o reconhecedor começou. */
  end: number;
}

export interface VoskFinal {
  text: string;
  result?: VoskWord[];
}

export interface VoskHit {
  palavra: string;
  conf: number;
  end: number;
  aceito: boolean;
}

/**
 * Gramática do Vosk: as palavras-chave mais `[unk]` (qualquer outra coisa).
 * Cada item pode ser uma frase ("manda entrar"); o Vosk aceita qualquer
 * sequência delas, e ignora palavra que não está no vocabulário do modelo.
 */
export function voskGrammar(palavras: string[]): string {
  return JSON.stringify([...palavras, '[unk]']);
}

export function voskHits(res: VoskFinal, palavras: string[], minConf: number): VoskHit[] {
  const alvo = new Set(palavras);
  return (res.result ?? [])
    .filter((w) => alvo.has(w.word))
    .map((w) => ({ palavra: w.word, conf: w.conf, end: w.end, aceito: w.conf >= minConf }));
}

/**
 * A frase que o Vosk entendeu, só com as palavras que contam: sem `[unk]`,
 * sem as iscas (palavras que estão na gramática só pra atrair o som parecido)
 * e sem palavra abaixo da confiança. Vazio = nada pra mandar adiante.
 */
export function voskTexto(res: VoskFinal, minConf: number, iscas: string[] = []): string {
  const fora = new Set(['[unk]', ...iscas]);
  return (res.result ?? [])
    .filter((w) => !fora.has(w.word) && w.conf >= minConf)
    .map((w) => w.word)
    .join(' ');
}
